import axios from 'axios';
import env from '../../../config';
import StarterKit from '@tiptap/starter-kit';
import { Button, Dialog, Portal, Stack, Text } from '@chakra-ui/react';
import { clientPanelInvitation } from '../../../operations/admin/clientPanelInvitation';
import { Control, RichTextEditor } from '../../../components/ui/rich-text-editor';
import { FiMail } from 'react-icons/fi';
import { renderEmail } from '../../../utils/renderEmail';
import { toaster } from '../../../components/ui/toaster';
import { useEditor } from '@tiptap/react';
import { useEffect, useMemo, useState } from 'react';
import { useEmailTemplatesList } from '../../../hooks/useEmailTemplates';

const defaultInvitationTemplate = 'Dzień dobry,<br/>zapraszamy do panelu klienta Opanuj.to.<br/>Aby ustawić hasło i zalogować się do panelu, kliknij tutaj:<br/>{{passwordResetLink}}';

type ClientPanelInvitationDialogProps = {
	client: any;
};

const ClientPanelInvitationDialog = ({ client }: ClientPanelInvitationDialogProps) => {
	const [isOpen, setOpen] = useState(false);
	const [isPending, setPending] = useState(false);
	const [templates] = useEmailTemplatesList();
	const invitationTemplate = useMemo(
		() => templates.find((template) => template.id === 'client-panel-invitation') ?? null,
		[templates],
	);
	const invitationTemplateHtml = invitationTemplate?.html || defaultInvitationTemplate;
	const editor = useEditor({
		extensions: [StarterKit],
		content: invitationTemplateHtml,
		immediatelyRender: false,
		editorProps: {
			attributes: {
				style: 'min-height: 180px; padding: 0.75rem 1rem; outline: none;',
			},
		},
	});

	useEffect(() => {
		if (!editor || !isOpen) {
			return;
		}

		if (editor.getHTML() !== invitationTemplateHtml) {
			editor.commands.setContent(invitationTemplateHtml, { emitUpdate: false });
		}
	}, [editor, invitationTemplateHtml, isOpen]);

	const sendInvitation = async () => {
		const userEmail = client?.email;
		if (!userEmail) {
			toaster.create({
				title: 'Brak adresu email klienta',
				type: 'error',
				duration: 3000,
			});
			return;
		}

		setPending(true);
		try {
			const passwordResetLink = await clientPanelInvitation({ userEmail });
			const html = renderEmail(editor?.getHTML() || invitationTemplateHtml, { passwordResetLink });
			await axios.post(env.endpoints.sendEmail, {
				to: userEmail,
				subject: invitationTemplate?.subject || 'Zaproszenie do panelu klienta Opanuj.to',
				html,
				bcc: ['sekretariat@opanuj.to', 'rsoszynski121@gmail.com'],
			}, {
				headers: { 'Content-Type': 'application/json' },
			});

			toaster.create({
				title: 'Zaproszenie wysłane',
				type: 'success',
				duration: 3000,
			});
			setOpen(false);
		} catch (error) {
			console.error('Client panel invitation failed', error);
			toaster.create({
				title: 'Nie udało się wysłać zaproszenia',
				description: 'Spróbuj ponownie.',
				type: 'error',
				duration: 4000,
			});
		} finally {
			setPending(false);
		}
	};

	return (
		<>
			<Button
				size="sm"
				variant="outline"
				colorPalette="orange"
				disabled={!client?.email}
				onClick={() => setOpen(true)}
			>
				<FiMail />
				Wyślij zaproszenie
			</Button>

			<Dialog.Root open={isOpen} onOpenChange={(event) => !event.open && setOpen(false)} role="dialog">
				<Portal>
					<Dialog.Backdrop />
					<Dialog.Positioner>
						<Dialog.Content maxW="900px" w="95vw">
							<Dialog.Header>
								<Dialog.Title>Wyślij zaproszenie do panelu</Dialog.Title>
							</Dialog.Header>
							<Dialog.Body>
								<Stack gap={3}>
									<Text fontSize="sm">Wiadomość zostanie wysłana do <strong>{client?.email}</strong>.</Text>
									<RichTextEditor.Root editor={editor}>
										<RichTextEditor.Toolbar justify="space-between" align="center">
											<RichTextEditor.ControlGroup>
												<Control.Bold />
												<Control.Italic />
												<Control.Underline />
												<Control.Link />
											</RichTextEditor.ControlGroup>
											<RichTextEditor.ControlGroup>
												<Control.H2 />
												<Control.BulletList />
												<Control.OrderedList />
											</RichTextEditor.ControlGroup>
										</RichTextEditor.Toolbar>
										<RichTextEditor.Content />
									</RichTextEditor.Root>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button variant="ghost" onClick={() => setOpen(false)} disabled={isPending}>Anuluj</Button>
								<Button colorPalette="orange" onClick={() => void sendInvitation()} loading={isPending}>Wyślij zaproszenie</Button>
							</Dialog.Footer>
						</Dialog.Content>
					</Dialog.Positioner>
				</Portal>
			</Dialog.Root>
		</>
	);
};

export default ClientPanelInvitationDialog;
